import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Spinner from "../common/Spinner/Spinner";
import userManager, { DEFAULT_RETURN_PATH, startSignIn, toSafeReturnPath } from "../auth/OIDCAuthHandler";
import { AuthCommandFacade } from "../auth/AuthCommandFacade";
import { refreshToken, SESSION_EXPIRED_MESSAGE } from "../auth/tokenRefreshService";
import { clearUserData, consumeAuthError, getToken } from "../../utils/persist";

const describeError = (error) => {
  const description = (error && (error.error_description || error.message)) || String(error);
  return error && error.error && !description.includes(error.error) ? `${error.error}: ${description}` : description;
};

// Handles /login and /signin-oidc: starts the authorization code + PKCE redirect, completes
// the IdP callback, and shows a visible reason when sign-in can't be completed.
const SigninOidcPage = ({ onSuccess }) => {
  const history = useHistory();
  const location = useLocation();
  const hasRun = useRef(false);
  const [authError, setAuthError] = useState(null);

  const requestedPath = toSafeReturnPath(new URLSearchParams(location.search).get("redirect"));

  const beginSignIn = () => {
    setAuthError(null);
    startSignIn(requestedPath);
  };

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const auth = new AuthCommandFacade();
    const params = new URLSearchParams(location.search);
    const isIdpCallback = params.has("code") || params.has("error");
    const token = getToken();

    const failSignIn = (message) => {
      clearUserData();
      userManager.clearStaleState().catch(() => {});
      setAuthError(message);
    };

    // A full page load lets App re-fetch permissions and access with the new token before routing.
    const completeSignIn = async (path) => {
      try {
        if (onSuccess) await onSuccess();
      } catch (error) {
        console.error("Sign-in logging failed:", error);
      }
      window.location.replace(path);
    };

    // An IdP response always wins over a previously stored (possibly stale) token.
    if (isIdpCallback) {
      userManager
        .signinRedirectCallback()
        .then((user) => {
          auth.login(user.access_token);
          return completeSignIn(toSafeReturnPath(user.state && user.state.returnTo));
        })
        .catch((error) => {
          console.error("OIDC sign-in callback failed:", error);
          if (token && auth.isTokenValid(token)) {
            history.replace(DEFAULT_RETURN_PATH);
          } else {
            failSignIn(`Sign-in failed. ${describeError(error)}`);
          }
        });
      return;
    }

    if (!token) {
      const previousError = consumeAuthError();
      if (previousError) {
        setAuthError(previousError);
      } else {
        startSignIn(requestedPath);
      }
      return;
    }

    if (auth.isTokenValid(token)) {
      history.replace(requestedPath);
      return;
    }

    refreshToken().then((newToken) =>
      newToken ? window.location.replace(requestedPath) : failSignIn(SESSION_EXPIRED_MESSAGE)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authError) {
    return (
      <div className="container mx-auto pt-24 px-4 text-center">
        <h1 className="text-xl font-bold mb-4">We couldn&apos;t sign you in</h1>
        <p role="alert" className="mb-6">
          {authError}
        </p>
        <button
          type="button"
          className="bg-orange-brand-500 hover:bg-gray-900 rounded text-white py-2 px-4"
          onClick={beginSignIn}
        >
          Sign in again
        </button>
      </div>
    );
  }

  return (
    <div className="pt-24 flex justify-center" role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">Signing you in…</span>
      <Spinner />
    </div>
  );
};

export default SigninOidcPage;
